import axios from 'axios';
import store from '@/store/index.js';

/**
 * Centralized session-validity monitor.
 *
 * Kicks a student out immediately (no manual refresh needed) when their
 * session is revoked server-side — admin Force Logout/Reset, login from
 * another device, auth_session_version bumped, or any other existing
 * server-side invalidation the auth.webstudent middleware already enforces.
 *
 * WHEN it asks (this module's only job):
 *   - once at app boot, if the client believes it's authenticated (init())
 *   - document.visibilitychange -> 'visible' (tab/WebView resumed — e.g. the
 *     student switched to WhatsApp and an admin revoked their session while away)
 *   - a 60s heartbeat, but ONLY while the current route is inside the
 *     authenticated student app (driven centrally by the router via
 *     startHeartbeat()/stopHeartbeat() — no per-page code required)
 *
 * WHAT happens on 401 (deliberately NOT this module's job): the existing
 * global axios response interceptor in main.js already performs the full
 * logout/cleanup/redirect flow for SESSION_REVOKED (and a generic fallback
 * scoped to this endpoint's own 401s). Keeping "when to ask" and "what an
 * invalid answer means" in one single place (the interceptor) avoids two
 * competing/duplicated logout code paths.
 *
 * Race protection: `inFlight` de-dupes visibilitychange + heartbeat + a manual
 * call landing at once into exactly one network request.
 */
const HEARTBEAT_MS = 60000;

let inFlight = null;
let heartbeatTimer = null;
let listenersBound = false;

function isAuthenticated() {
  return !!store.getters.isAuthenticated;
}

/** Ask the backend whether the current session is still valid. De-duped. */
function checkAuth() {
  if (!isAuthenticated()) return Promise.resolve(false);
  if (inFlight) return inFlight;

  inFlight = axios.get('/auth/check-auth')
    // A 401 here is fully handled by the global response interceptor
    // (logout + redirect + message) — swallow it here so this module never
    // races that flow or retries/recurses into another check-auth call.
    .then(() => true)
    .catch(() => false)
    .finally(() => { inFlight = null; });

  return inFlight;
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    checkAuth();
  }
}

/** Start the 60s heartbeat (idempotent; no-op if already running or logged out). */
function startHeartbeat() {
  if (heartbeatTimer || !isAuthenticated()) return;
  heartbeatTimer = setInterval(() => {
    if (!isAuthenticated()) {
      stopHeartbeat();
      return;
    }
    checkAuth();
  }, HEARTBEAT_MS);
}

/** Stop the heartbeat (called when leaving the authenticated app, or on logout). */
function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/** Called once at app boot (main.js). Binds the visibilitychange listener once. */
function init() {
  if (isAuthenticated()) {
    checkAuth();
  }
  if (!listenersBound) {
    // `visibilitychange` is a standard DOM event supported by all Android
    // WebView versions this app targets — no new native/Flutter code needed.
    document.addEventListener('visibilitychange', onVisibilityChange);
    listenersBound = true;
  }
}

export default { init, checkAuth, startHeartbeat, stopHeartbeat };
